import {Component, Input, OnChanges, OnInit, ViewChild} from '@angular/core';
import {Store} from '@ngrx/store';
import {AppState} from 'app/app.reducer';
import {Router} from '@angular/router';
import {eq, find, head} from 'lodash';
import {Paginator} from 'primeng/paginator';
import {AuthenticationService, AuthorService, OutletService, SearchService, UserLabelService} from 'app/services';
import * as actions from 'redux/actions';
import {FacetSearch, Notification, Search} from 'app/models';
import {Content, SPINNER_ICON, STAR_ICON, STAR_ICON_FILL} from 'app/constants';

@Component({
  selector: 'facet-outlet-content',
  templateUrl: './facet-outlet-content.component.html',
  styleUrls: ['./facet-outlet-content.component.scss'],
})
export class FacetOutletContentComponent implements OnInit, OnChanges {
  @Input() contentData: any;
  public searchBody: Search;
  public sortSelected: string;
  public outletContentPage: number;
  public outletContentTotalPages: number;
  public outletContentPageSize: number;
  public outletContentTotal: number;
  public currentPage: number;
  public hideFollowing: boolean;
  public authorsOutletsList: any[];
  public project: any;
  public isOutletSaved: boolean;
  public starToggle: string;
  public loading: boolean;
  public outlet: any;
  public outletPicture: string;
  public contentItems: any[] = [];
  public actionType: string;
  public savedOutlet: any;
  public categories: any[] = [];
  public readonly shortcutLabels = ['Primary', 'Secondary'];

  @ViewChild('pp', { static: false }) paginator: Paginator;
  public parentName: Content;

  constructor(
    private outletService: OutletService,
    private searchService: SearchService,
    private authorService: AuthorService,
    private router: Router,
    private store: Store<AppState>,
    private auth: AuthenticationService,
    private userLabelService: UserLabelService
  ) {
    this.parentName = Content.Outlet;
    this.sortSelected = 'recent';
    this.loading = false;
    this.starToggle = STAR_ICON;
    this.isOutletSaved = false;
    this.authorsOutletsList = [];
    this.project = null;
    this.actionType = null;
    this.searchBody = null;
  }

  ngOnInit() {
    this.hideFollowing = false;
    this.outletContentPage = 1;
    this.currentPage = 0;

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.project = projectSelected;
      }
    });

    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        this.searchBody = searchBody;
      }
    });

    this.store.select('category').subscribe(({ categories }) => {
      this.categories = categories || [];
    });

    this.store
      .select('authorsOutletsList')
      .subscribe(
        ({
          outlets,
          isAddingAuthorOutlet,
          isDeletingAuthorOutlet,
          hasAuthorOutletDeleted,
          hasAuthorOutletAdded,
          errors,
        }) => {
          this.authorsOutletsList = outlets;

          if (isAddingAuthorOutlet || isDeletingAuthorOutlet) {
            this.starToggle = SPINNER_ICON;
          }

          if (hasAuthorOutletAdded) {
            this.setOutletSaved();
          }

          if (hasAuthorOutletDeleted) {
            this.setOutletSaved();
          }

          if (errors?.message) {
            this.store.dispatch(
              actions.showNotification({
                notify: new Notification(
                  `Error ${this.actionType} outlet to project`,
                  'error',
                  'tl'
                ),
              })
            );
            this.setOutletSaved();
            setTimeout(() => {
              this.store.dispatch(actions.initAuthorOutletToProjectState());
            }, 500);
          }
        }
      );
  }

  ngOnChanges(changes: any) {
    const { outlet, contentItems, facetSearch } =
      changes.contentData?.currentValue;
    const author = head(contentItems.content)?.author;
    const authorId = head(contentItems.content)?.authorId;
    this.outlet = {
      ...outlet,
      author,
      authorId,
      picture: this.outletService.getOutletPicture(outlet.id),
    };
    this.outletPicture = this.outlet.picture;
    this.outletService.getOutletById(outlet.id).subscribe((response: any) => {
      this.outlet = response;
    });
    this.outletContentTotalPages = contentItems.totalPages;
    this.outletContentTotal = contentItems.totalElements;
    this.contentItems = contentItems.content;
    this.outletContentPageSize = facetSearch.size;
    this.currentPage = facetSearch.page;
    this.setOutletSaved();
    this.resetPaginator();
  }

  public goToExploreRelatedCoverage(outlet) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/outlet/${outlet.id}`])
    );

    window.open(url, '_blank');
  }

  public sortChange(e) {
    this.sortSelected = e;
    this.outletContentPage = 1;
    this.currentPage = 0;
    this.resetPaginator();
    this.getOutletContent();
  }

  getPage(e: any) {
    this.outletContentPage = e.page + 1;
    this.currentPage = e.page;
    this.getOutletContent();
  }

  getOutletContent() {
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
      }),
      sort: this.sortSelected,
      page: this.currentPage,
      size: this.outletContentPageSize,
    });

    this.searchService.searchContent(facetSearch).subscribe((response) => {
      this.outletContentTotalPages = response.totalPages;
      this.outletContentTotal = response.totalElements;
      this.contentItems = response.content;
      // this.scrollTop();
    });
  }

  resetPaginator() {
    if (this.paginator) {
      this.paginator.changePage(0);
    }
  }

  private setOutletSaved() {
    setTimeout(() => {
      this.savedOutlet = find(this.authorsOutletsList, (i) =>
        eq(i.partyId, this.outlet.id)
      );
      this.isOutletSaved = !!this.savedOutlet;
      this.starToggle = this.isOutletSaved ? STAR_ICON_FILL : STAR_ICON;
    }, 500);
  }

  handleSaveOutlet(outlet) {
    if (this.isOutletSaved) {
      this.actionType = 'deleting';
      this.store.dispatch(
        actions.deleteOutletToProject({
          partyId: outlet.id,
          userProject: this.project,
          outlet,
        })
      );
    } else {
      this.actionType = 'adding';
      this.store.dispatch(
        actions.addOutletToProject({
          partyId: outlet.id,
          userProject: this.project,
          outlet,
        })
      );
    }
  }

  handleIgnoreOutlet(currentlyIgnored: boolean) {
    if (currentlyIgnored) {
      this.handleSaveOutlet(this.outlet);
      return;
    }
    const projectId = this.project?.value || this.project?.id;
    this.userLabelService.getLabel('Ignore', projectId).subscribe({
      next: (ignore) => {
        if (!ignore) {
          this.showLabelError('Ignore');
          return;
        }
        this.store.dispatch(
          actions.addOutletToProject({
            partyId: this.outlet.id,
            userProject: this.project,
            outlet: this.outlet,
            userLabelId: ignore.id,
          })
        );
      },
      error: () => this.showLabelError('Ignore'),
    });
  }

  handleOutletLabel(labelName: string) {
    const category = this.findCategory(labelName);
    if (!category || !this.savedOutlet) {
      this.showLabelError(labelName);
      return;
    }
    this.store.dispatch(
      actions.updateOutletLabel({
        userPartyId: this.savedOutlet.id,
        userProject: this.project,
        outlet: this.outlet,
        userLabelId: this.outletLabel === labelName ? null : category.id,
      })
    );
  }

  get isOutletIgnored(): boolean {
    return this.outletLabel === 'Ignore';
  }

  get outletLabel(): string {
    return (
      this.savedOutlet?.categoryName || this.savedOutlet?.label?.name || null
    );
  }

  get isSavingOutlet(): boolean {
    return this.starToggle === SPINNER_ICON;
  }

  private findCategory(name: string): any {
    return find(
      this.categories,
      (category) => String(category.name).toLowerCase() === name.toLowerCase()
    );
  }

  private showLabelError(labelName: string): void {
    this.store.dispatch(
      actions.showNotification({
        notify: new Notification(
          `The ${labelName} label is not available`,
          'error',
          'tl'
        ),
      })
    );
  }

  openReportMissingDataModal() {
    this.store.dispatch(actions.showReportDialog());
  }

  hasReportingAccess() {
    return this.auth.hasReportingAccess();
  }
}
